// The deck editor's DOCUMENT: a decklist text split into the parts the editor
// edits (the main deck and the Maybeboard) and the parts it only carries
// (the command zone, companions and every other non-deck line).
//
// Built on lib/decklist.ts: the same section scanner, the same classifier and
// the same serializer, so the editor can never read a header two ways. The
// Maybeboard used to ride inside extractNonDeckSections' opaque string; here
// it is folded into editable rows instead:
//
//  - EVERY maybeboard block (any header classifyHeader maps to 'maybeboard':
//    "// Maybeboard (5)", "Maybeboard:", "Considering", "[MAYBE]", ...) is
//    folded into `maybe`, duplicates summed by DFC front face, the first
//    spelling kept.
//  - `passthrough` is extractNonDeckSections MINUS those blocks: the
//    sideboard, tokens and the loose non-deck lines ("SB: ...", {noDeck}
//    tags), verbatim and in order.
//  - serializeDeckDocument writes ONE "// Maybeboard" block, where the first
//    maybeboard block was (`maybeAt`), or appended last. An empty maybeboard
//    writes no block at all.
//
// For a deck with no maybeboard the output is byte for byte what
// serializeDecklist(parseDecklist(x), zone, extractNonDeckSections(x),
// companions) produced before, and a second parse + serialize of any output
// returns the same text.
//
// An UNTOUCHED Maybeboard is never rewritten: the document remembers the
// non-deck text exactly as parsed (`maybeOrigin`), and while the Maybeboard
// rows (and the passthrough) still equal what was parsed, that text is
// written back byte for byte, comments, set / foil / category markers,
// header text and line order included. A main-deck edit therefore never
// touches it.
//
// Once the Maybeboard rows change it is normalized into the one block, but
// each surviving row keeps its ORIGINAL line ("1 Sol Ring (C21) 263 *F*"):
// as written when its count is unchanged, with only the count rewritten
// when it moved. New rows are "N Name". What a normalized block cannot keep:
// its comment and blank lines (the main deck's comments are dropped by the
// serializer the same way). A Commander-tagged card under a maybeboard
// header is never a Maybeboard row: it is a deck card to the API, so it is
// the command zone's.
import {
  commandZone as commandZoneOf,
  extractCompanions,
  extractNonDeckSections,
  frontKey,
  isNonDeckSection,
  parseDecklist,
  scanDecklist,
  serializeDecklist,
  stripNameMarkers,
  type EditableCard,
} from './decklist'

export interface DeckDocument {
  /** Commander / partner names, locked. */
  commandZone: string[]
  /** Companions, their own locked zone. */
  companions: string[]
  /** The main deck: exactly parseDecklist's rows. */
  main: EditableCard[]
  /** The Maybeboard, folded from every maybeboard block. */
  maybe: EditableCard[]
  /** Every other non-deck block, verbatim and in order. */
  passthrough: string
  /**
   * Where the Maybeboard block goes: a character offset into `passthrough`
   * at a block boundary (0 = before every block). Absent, or no longer a
   * boundary of `passthrough`, the block is appended last.
   */
  maybeAt?: number
  /**
   * The Maybeboard as parsed, so an untouched one round-trips verbatim (see
   * the module comment). Present only when the text had a maybeboard.
   */
  maybeOrigin?: MaybeOrigin
}

export interface MaybeOrigin {
  /** The parsed rows, to tell an untouched Maybeboard from an edited one. */
  snapshot: EditableCard[]
  /** extractNonDeckSections of the parsed text: the verbatim output. */
  nonDeck: string
  /** The passthrough as parsed (an edited passthrough is not verbatim either). */
  passthrough: string
  /** Each row's original card lines (trimmed), by front face, in order. */
  lines: Record<string, string[]>
}

/* eslint-disable i18next/no-literal-string -- decklist syntax and section codes, never shown */
const MAYBE_HEADER = '// Maybeboard'
const QTY_RE = /^(\d+)\s*[xX]?\s+(.+)$/
/* eslint-enable i18next/no-literal-string */

/**
 * The document for a decklist TEXT. `rowCommanders` are the row's own
 * commander / partner fields, merged into the command zone the text names
 * (see commandZone in lib/decklist.ts).
 */
export function parseDeckDocument(
  text: string | null | undefined,
  ...rowCommanders: (string | null | undefined)[]
): DeckDocument {
  const src = text ?? ''
  const { passthrough, maybe, maybeAt, lines } = splitNonDeck(src)
  const doc: DeckDocument = {
    commandZone: commandZoneOf(src, ...rowCommanders),
    companions: extractCompanions(src),
    main: parseDecklist(src),
    maybe,
    passthrough,
  }
  if (maybeAt !== undefined) {
    doc.maybeAt = maybeAt
    doc.maybeOrigin = {
      snapshot: maybe.map(c => ({ ...c })),
      nonDeck: extractNonDeckSections(src),
      passthrough,
      lines,
    }
  }
  return doc
}

/** The document as decklist text (see the module comment for the layout). */
export function serializeDeckDocument(doc: DeckDocument): string {
  return serializeDecklist(doc.main, doc.commandZone, nonDeckText(doc), doc.companions)
}

/**
 * The Maybeboard block alone ('' when it is empty). With the original lines
 * (MaybeOrigin.lines), a row keeps its own line text: verbatim at the same
 * count, the count prefix rewritten when it changed.
 */
export function serializeMaybeboard(maybe: readonly EditableCard[], lines?: Record<string, string[]>): string {
  const rows = maybe.filter(c => c.quantity > 0 && c.name.trim())
  if (rows.length === 0) return ''
  const out: string[] = [MAYBE_HEADER]
  for (const c of rows) {
    const orig = lines?.[frontKey(c.name)]
    if (!orig || orig.length === 0) { out.push(`${c.quantity} ${c.name}`); continue }
    if (orig.reduce((n, l) => n + lineQty(l), 0) === c.quantity) { out.push(...orig); continue }
    // A changed count: the first line carries it, its markers kept.
    out.push(withQty(orig[0], c.quantity))
  }
  return out.join('\n')
}

function lineQty(line: string): number {
  const m = line.match(QTY_RE)
  return m ? parseInt(m[1], 10) || 1 : 1
}

// The line with its count prefix replaced ("2x Sol Ring [C21]" at 3 ->
// "3x Sol Ring [C21]"); a line with no count gets one.
function withQty(line: string, qty: number): string {
  const m = line.match(/^(\d+)(\s*[xX]?\s+)(.+)$/)
  return m ? `${qty}${m[2]}${m[3]}` : `${qty} ${line}`
}

/** Same rows and counts, by front face, in any order. */
function sameBoard(a: readonly EditableCard[], b: readonly EditableCard[]): boolean {
  const tally = (cards: readonly EditableCard[]) => {
    const m = new Map<string, number>()
    for (const c of cards) if (c.quantity > 0) m.set(frontKey(c.name), (m.get(frontKey(c.name)) ?? 0) + c.quantity)
    return m
  }
  const ma = tally(a)
  const mb = tally(b)
  if (ma.size !== mb.size) return false
  for (const [k, q] of ma) if (mb.get(k) !== q) return false
  return true
}

/** Total copies in a board. */
export function boardCount(cards: readonly EditableCard[]): number {
  return cards.reduce((n, c) => n + c.quantity, 0)
}

// The passthrough with the Maybeboard block placed in it.
function nonDeckText(doc: DeckDocument): string {
  const origin = doc.maybeOrigin
  const pass = doc.passthrough ?? ''
  // Untouched: the non-deck text exactly as parsed.
  if (origin && pass === origin.passthrough && sameBoard(doc.maybe, origin.snapshot)) return origin.nonDeck
  const block = serializeMaybeboard(doc.maybe, origin?.lines)
  if (!block) return pass
  const at = doc.maybeAt
  const boundary = at !== undefined && at >= 0 && at <= pass.length
    && (at === 0 || at === pass.length || pass.slice(at, at + 2) === '\n\n')
  if (!boundary) return [pass, block].filter(s => s.trim()).join('\n\n')
  const before = pass.slice(0, at)
  const after = pass.slice(at).replace(/^\n+/, '')
  return [before, block, after].filter(s => s.trim()).join('\n\n')
}

// extractNonDeckSections' walk (lib/decklist.ts), with the maybeboard
// sub-regions taken out: their card lines become `maybe`, their loose lines
// stay passthrough, their comments and blanks go. Without a maybeboard header
// this is extractNonDeckSections line for line, which is what keeps a
// maybeboard-less deck byte-identical.
function splitNonDeck(text: string): { passthrough: string; maybe: EditableCard[]; maybeAt?: number; lines: Record<string, string[]> } {
  const blocks: string[][] = []
  let current: string[] | null = null
  // Under a non-deck header (up to the next deck header).
  let region = false
  // Under a maybeboard header specifically (up to the next header).
  let inMaybe = false
  let maybeAtBlock: number | null = null
  const maybe: EditableCard[] = []
  const maybeIndex = new Map<string, number>()
  const lines: Record<string, string[]> = {}

  const flush = () => {
    if (current) {
      while (current.length > 0 && current[current.length - 1].trim() === '') current.pop()
      if (current.length > 0) blocks.push(current)
    }
    current = null
  }
  const close = () => {
    flush()
    region = false
    inMaybe = false
  }
  const addMaybe = (line: string) => {
    let qty = 1
    let rest = line
    const m = line.match(QTY_RE)
    if (m) {
      qty = parseInt(m[1], 10) || 1
      rest = m[2]
    }
    const name = stripNameMarkers(rest)
    if (!name) return
    const key = frontKey(name)
    ;(lines[key] ??= []).push(line)
    const at = maybeIndex.get(key)
    if (at !== undefined) maybe[at] = { ...maybe[at], quantity: maybe[at].quantity + qty }
    else {
      maybeIndex.set(key, maybe.length)
      maybe.push({ name, quantity: qty })
    }
  }

  for (const l of scanDecklist(text)) {
    if (l.kind === 'header') {
      if (!isNonDeckSection(l.section)) { close(); continue }
      // eslint-disable-next-line i18next/no-literal-string -- a section code
      if (l.section === 'maybeboard') {
        // The passthrough piece so far ends here; the Maybeboard sits at
        // this boundary (the FIRST such header decides).
        flush()
        if (maybeAtBlock === null) maybeAtBlock = blocks.length
        region = true
        inMaybe = true
        continue
      }
      // A sideboard / tokens header: a passthrough block again. Leaving a
      // maybeboard region starts a fresh piece after it.
      if (inMaybe) { flush(); inMaybe = false }
      if (!current) current = []
      region = true
      current.push(l.raw)
      continue
    }
    if (region && inMaybe) {
      // eslint-disable-next-line i18next/no-literal-string -- a section code
      if (l.kind === 'card' && l.section === 'maybeboard') addMaybe(l.line)
      else if (l.kind === 'loose') {
        if (!current) current = []
        current.push(l.raw)
      }
      // Comments, blanks and a Commander-tagged deck card: not the
      // Maybeboard's.
      continue
    }
    if (region) { current!.push(l.raw); continue }
    if (l.kind === 'loose') {
      if (!current) current = []
      current.push(l.raw)
      continue
    }
    if (current && l.kind === 'blank') { current.push(l.raw); continue }
    // A deck card or comment ends a loose-only block.
    if (current) close()
  }
  close()

  const parts = blocks.map(b => b.join('\n'))
  const passthrough = parts.join('\n\n')
  if (maybeAtBlock === null) return { passthrough, maybe, lines }
  const maybeAt = maybeAtBlock === 0 ? 0 : parts.slice(0, maybeAtBlock).join('\n\n').length
  return { passthrough, maybe, maybeAt, lines }
}

/**
 * The document's non-board state: everything a Text-mode apply can replace
 * besides the two boards (the passthrough blocks, where the Maybeboard block
 * sits in them, and the Maybeboard's verbatim origin). One undo step carries
 * the before / after pair, so Undo restores the sideboard, tokens and notes
 * together with the cards.
 */
export interface NonDeckState {
  passthrough: string
  maybeAt?: number
  maybeOrigin?: MaybeOrigin
}

/** Same non-board state (the origin compared by its verbatim text and rows). */
export function sameNonDeck(a: NonDeckState, b: NonDeckState): boolean {
  if (a.passthrough !== b.passthrough || a.maybeAt !== b.maybeAt) return false
  const oa = a.maybeOrigin
  const ob = b.maybeOrigin
  if (!oa || !ob) return !oa && !ob
  return oa === ob || (oa.nonDeck === ob.nonDeck && oa.passthrough === ob.passthrough && sameBoard(oa.snapshot, ob.snapshot))
}

/**
 * The text Text mode edits: the document WITHOUT its command zone and
 * companions (those are shown read-only and never edited there). The main
 * deck's command-zone rows are left out too; the non-deck part is exactly
 * what serializeDeckDocument writes, so an untouched Maybeboard reads back
 * byte for byte.
 */
export function editableDeckText(doc: DeckDocument): string {
  const locked = new Set([...doc.commandZone, ...doc.companions].map(frontKey))
  return serializeDecklist(doc.main.filter(c => !locked.has(frontKey(c.name))), [], nonDeckText(doc), [])
}
